import { Directive, ElementRef, Injector, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { CrudDataSource, CrudListComponent, FormStorage, UserStorage } from '@scisys-uim/uim-common';
import { ASSET_METADATA, ASSET_TYPE, Asset, AssetsUser, BUSINESS_UNIT_METADATA, BUSINESS_UNIT_TYPE, BusinessUnit, COST_CENTRE_METADATA, COST_CENTRE_TYPE, CostCentre, TRANSFER_ASSET_TYPE, TransferAsset } from 'assets-model';
import { AssetService, BusinessUnitService, CostCentreService, TransferAssetDataSource, TransferAssetService } from 'assets-service';


@Directive()
export abstract class TransferAssetSearchComponentBase extends CrudListComponent<TransferAsset, number> {
  protected systemConfig: any;

  displayedColumns: string[] = [ 'transferAssetId', 'asset.assetId', 'businessUnit.buId', 'costCentre.ccId', 'approvalRejectionComment', 'approved', 'approvedRejectedBy', 'approvedRejectedDate', 'assetSrcBusinessUnit', 'assetSrcCostCentre', 'comments', 'createdBy', 'dateCreated', 'lockVersion', 'actions'];

  public searchForm: FormGroup;

  transferAssetIdFilter: number;
  @ViewChild('transferAssetIdFilterInput') transferAssetIdFilterInput: ElementRef;

  assetFilter: Asset;
  assetSelectionList: Asset[];
  @ViewChild('assetFilterInput') assetFilterInput: MatSelect;

  businessUnitFilter: BusinessUnit;
  businessUnitSelectionList: BusinessUnit[];
  @ViewChild('businessUnitFilterInput') businessUnitFilterInput: MatSelect;

  costCentreFilter: CostCentre;
  costCentreSelectionList: CostCentre[];
  @ViewChild('costCentreFilterInput') costCentreFilterInput: MatSelect;

  approvalRejectionCommentFilter: string;
  @ViewChild('approvalRejectionCommentFilterInput') approvalRejectionCommentFilterInput: ElementRef;

  approvedFilter: boolean;
  @ViewChild('approvedFilterInput') approvedFilterInput: MatSelect;

  approvedRejectedByFilter: string;
  @ViewChild('approvedRejectedByFilterInput') approvedRejectedByFilterInput: ElementRef;

  approvedRejectedDateFilter: Date;
  @ViewChild('approvedRejectedDateFilterInput') approvedRejectedDateFilterInput: ElementRef;
  approvedRejectedDateLessThanFilter: Date;
  @ViewChild('approvedRejectedDateLessThanFilterInput') approvedRejectedDateLessThanFilterInput: ElementRef;
  approvedRejectedDateLessThanOrEqualToFilter: Date;
  @ViewChild('approvedRejectedDateLessThanOrEqualToFilterInput') approvedRejectedDateLessThanOrEqualToFilterInput: ElementRef;
  approvedRejectedDateGreaterThanFilter: Date;
  @ViewChild('approvedRejectedDateGreaterThanFilterInput') approvedRejectedDateGreaterThanFilterInput: ElementRef;
  approvedRejectedDateGreaterThanOrEqualToFilter: Date;
  @ViewChild('approvedRejectedDateGreaterThanOrEqualToFilterInput') approvedRejectedDateGreaterThanOrEqualToFilterInput: ElementRef;

  commentsFilter: string;
  @ViewChild('commentsFilterInput') commentsFilterInput: ElementRef;

  createdByFilter: string;
  @ViewChild('createdByFilterInput') createdByFilterInput: ElementRef;

  dateCreatedFilter: Date;
  @ViewChild('dateCreatedFilterInput') dateCreatedFilterInput: ElementRef;
  dateCreatedLessThanFilter: Date;
  @ViewChild('dateCreatedLessThanFilterInput') dateCreatedLessThanFilterInput: ElementRef;
  dateCreatedLessThanOrEqualToFilter: Date;
  @ViewChild('dateCreatedLessThanOrEqualToFilterInput') dateCreatedLessThanOrEqualToFilterInput: ElementRef;
  dateCreatedGreaterThanFilter: Date;
  @ViewChild('dateCreatedGreaterThanFilterInput') dateCreatedGreaterThanFilterInput: ElementRef;
  dateCreatedGreaterThanOrEqualToFilter: Date;
  @ViewChild('dateCreatedGreaterThanOrEqualToFilterInput') dateCreatedGreaterThanOrEqualToFilterInput: ElementRef;


  get eAssetsUser(): AssetsUser {
    return AssetsUser.assignAs(this.userDetails);
  }

  constructor(protected route: ActivatedRoute,
    protected router: Router,
    protected userStorage: UserStorage,
    protected dialog: MatDialog,
    protected transferAssetService: TransferAssetService,
    protected assetService: AssetService,
    protected businessUnitService: BusinessUnitService,
    protected costCentreService: CostCentreService,
    protected fb: FormBuilder,
    protected formStorage: FormStorage,
    protected injector: Injector) {
    super(TRANSFER_ASSET_TYPE, route, router, userStorage, dialog, transferAssetService, injector);

    this.searchForm = this.fb.group({
      transferAssetId: [null],
      asset: [null],
      businessUnit: [null],
      costCentre: [null],
      approvalRejectionComment: [null],
      approved: [null],
      approvedRejectedBy: [null],
      approvedRejectedDateLessThan: [null],
      approvedRejectedDateLessThanOrEqualTo: [null],
      approvedRejectedDateGreaterThan: [null],
      approvedRejectedDateGreaterThanOrEqualTo: [null],
      approvedRejectedDate: [null],
      comments: [null],
      createdBy: [null],
      dateCreatedLessThan: [null],
      dateCreatedLessThanOrEqualTo: [null],
      dateCreatedGreaterThan: [null],
      dateCreatedGreaterThanOrEqualTo: [null],
      dateCreated: [null]
    });
  }

  createDataSource(): CrudDataSource<TransferAsset> {
    return new TransferAssetDataSource(this.transferAssetService);
  }

  createServiceArguments(): any {
    return {
      transferAssetId: this.transferAssetIdFilter,
      assetId: this.assetFilter && this.assetFilter.assetId,
      buId: this.businessUnitFilter && this.businessUnitFilter.buId,
      ccId: this.costCentreFilter && this.costCentreFilter.ccId,
      approvalRejectionComment: this.approvalRejectionCommentFilter,
      approved: this.approvedFilter,
      approvedRejectedBy: this.approvedRejectedByFilter,
      approvedRejectedDateLessThan: this.approvedRejectedDateLessThanFilter,
      approvedRejectedDateLessThanOrEqualTo: this.approvedRejectedDateLessThanOrEqualToFilter,
      approvedRejectedDateGreaterThan: this.approvedRejectedDateGreaterThanFilter,
      approvedRejectedDateGreaterThanOrEqualTo: this.approvedRejectedDateGreaterThanOrEqualToFilter,
      approvedRejectedDate: this.approvedRejectedDateFilter,
      comments: this.commentsFilter,
      createdBy: this.createdByFilter,
      dateCreatedLessThan: this.dateCreatedLessThanFilter,
      dateCreatedLessThanOrEqualTo: this.dateCreatedLessThanOrEqualToFilter,
      dateCreatedGreaterThan: this.dateCreatedGreaterThanFilter,
      dateCreatedGreaterThanOrEqualTo: this.dateCreatedGreaterThanOrEqualToFilter,
      dateCreated: this.dateCreatedFilter
    };
  }
  
  clearFiltersBeforeLoadPage() {
    this.restoreFormFromParams(this.searchForm);
  }
  
  clearFilterVariables() {
    this.transferAssetIdFilter = null;
    this.assetFilter = null;
    this.businessUnitFilter = null;
    this.costCentreFilter = null;
    this.approvalRejectionCommentFilter = null;
    this.approvedFilter = null;
    this.approvedRejectedByFilter = null;
    this.approvedRejectedDateLessThanFilter = null;
    this.approvedRejectedDateLessThanOrEqualToFilter = null;
    this.approvedRejectedDateGreaterThanFilter = null;
    this.approvedRejectedDateGreaterThanOrEqualToFilter = null;
    this.approvedRejectedDateFilter = null;
    this.commentsFilter = null;
    this.createdByFilter = null;
    this.dateCreatedLessThanFilter = null;
    this.dateCreatedLessThanOrEqualToFilter = null;
    this.dateCreatedGreaterThanFilter = null;
    this.dateCreatedGreaterThanOrEqualToFilter = null;
    this.dateCreatedFilter = null;
  }
  
  clearFilters() {
    this.clearFilterVariables();
    this.clearFiltersBeforeLoadPage();
    this.loadPage();
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.addSearchOnChangedInputEventListener(this.transferAssetIdFilterInput);
    this.addSearchOnChangedSelectEventListener(this.assetFilterInput);
    this.addSearchOnChangedSelectEventListener(this.businessUnitFilterInput);
    this.addSearchOnChangedSelectEventListener(this.costCentreFilterInput);
    this.addSearchOnChangedInputEventListener(this.approvalRejectionCommentFilterInput);
    this.addSearchOnChangedSelectEventListener(this.approvedFilterInput);
    this.addSearchOnChangedInputEventListener(this.approvedRejectedByFilterInput);
    this.addSearchOnChangedInputEventListener(this.approvedRejectedDateLessThanFilterInput);
    this.addSearchOnChangedInputEventListener(this.approvedRejectedDateLessThanOrEqualToFilterInput);
    this.addSearchOnChangedInputEventListener(this.approvedRejectedDateGreaterThanFilterInput);
    this.addSearchOnChangedInputEventListener(this.approvedRejectedDateGreaterThanOrEqualToFilterInput);
    this.addSearchOnChangedInputEventListener(this.approvedRejectedDateFilterInput);
    this.addSearchOnChangedInputEventListener(this.commentsFilterInput);
    this.addSearchOnChangedInputEventListener(this.createdByFilterInput);
    this.addSearchOnChangedInputEventListener(this.dateCreatedLessThanFilterInput);
    this.addSearchOnChangedInputEventListener(this.dateCreatedLessThanOrEqualToFilterInput);
    this.addSearchOnChangedInputEventListener(this.dateCreatedGreaterThanFilterInput);
    this.addSearchOnChangedInputEventListener(this.dateCreatedGreaterThanOrEqualToFilterInput);
    this.addSearchOnChangedInputEventListener(this.dateCreatedFilterInput);
    this.formStorage.rememberFormChanges(this.searchForm);
  }

  ngOnInit() {
    super.ngOnInit();
  }
  
  protected onInitAsyncComplete() {
    super.onInitAsyncComplete();
  }

  protected initExportOptions(config: any): any {
    config = config || {};
    return config;
  }

  protected initConfig(config: any) {
    super.initConfig(config);
    this.systemConfig = this.initExportOptions(config);
  }
  
  protected transformAllPropertiesToModelSetters(): {
    [key: string]: (value: any, formControl: FormControl, formControlName: string) => void
  } {
    const self = this;
    return {
      transferAssetId: (value: number) => self.transferAssetIdFilter = value,
      asset: (value: Asset) => { self.assetFilter = value; this.assetChanged(value, false); },
      businessUnit: (value: BusinessUnit) => { self.businessUnitFilter = value; this.businessUnitChanged(value, false); },
      costCentre: (value: CostCentre) => { self.costCentreFilter = value; this.costCentreChanged(value, false); },
      approvalRejectionComment: (value: string) => self.approvalRejectionCommentFilter = value,
      approved: (value: boolean) => self.approvedFilter = value,
      approvedRejectedBy: (value: string) => self.approvedRejectedByFilter = value,
      approvedRejectedDateLessThan: (value: Date) => self.approvedRejectedDateLessThanFilter = value,
      approvedRejectedDateLessThanOrEqualTo: (value: Date) => self.approvedRejectedDateLessThanOrEqualToFilter = value,
      approvedRejectedDateGreaterThan: (value: Date) => self.approvedRejectedDateGreaterThanFilter = value,
      approvedRejectedDateGreaterThanOrEqualTo: (value: Date) => self.approvedRejectedDateGreaterThanOrEqualToFilter = value,
      approvedRejectedDate: (value: Date) => self.approvedRejectedDateFilter = value,
      comments: (value: string) => self.commentsFilter = value,
      createdBy: (value: string) => self.createdByFilter = value,
      dateCreatedLessThan: (value: Date) => self.dateCreatedLessThanFilter = value,
      dateCreatedLessThanOrEqualTo: (value: Date) => self.dateCreatedLessThanOrEqualToFilter = value,
      dateCreatedGreaterThan: (value: Date) => self.dateCreatedGreaterThanFilter = value,
      dateCreatedGreaterThanOrEqualTo: (value: Date) => self.dateCreatedGreaterThanOrEqualToFilter = value,
      dateCreated: (value: Date) => self.dateCreatedFilter = value
    }
  }
  
  restoreFormChanges() {
    const self = this;

    // IMPORTANT that the restoreFormChanges call occurs before the initial search - typically best done in onInitAsyncComplete
    // Whereever a form control binds with [ngModel] to a reactive form, ngModel takes precedence,
    // and needs it's own setter function to set the scope attribute bound
    this.formStorage.restoreFormChanges(this.searchForm, this.transformAllPropertiesToModelSetters());
  }

  transferAssetIdChanging(value: number, unsetDependents: boolean = true) {}
  
  transferAssetIdChanged(value: number, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  assetLoadSelectionList() {
    this.assetService.findAll().subscribe(
      data => {
        this.assetSelectionList = data.items;
      }
    );
  }
  
  assetDisplay(item: Asset) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return ASSET_TYPE.description(item);
  }
  
  assetChanging(value: Asset, unsetDependents: boolean = true) {}
  
  assetChanged(value: Asset, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }
  
  assetCompare(a: any, b: any) {
    return ASSET_METADATA.equals(a, b);
  }
  
  

  businessUnitLoadSelectionList() {
    this.businessUnitService.findAll().subscribe(
      data => {
        this.businessUnitSelectionList = data.items;
      }
    );
  }
  
  businessUnitDisplay(item: BusinessUnit) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return BUSINESS_UNIT_TYPE.description(item);
  }
  
  businessUnitChanging(value: BusinessUnit, unsetDependents: boolean = true) {}
  
  businessUnitChanged(value: BusinessUnit, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }
  
  businessUnitCompare(a: any, b: any) {
    return BUSINESS_UNIT_METADATA.equals(a, b);
  }
  
  

  costCentreLoadSelectionList() {
    this.costCentreService.findAll().subscribe(
      data => {
        this.costCentreSelectionList = data.items;
      }
    );
  }
  
  costCentreDisplay(item: CostCentre) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return COST_CENTRE_TYPE.description(item);
  }
  
  costCentreChanging(value: CostCentre, unsetDependents: boolean = true) {}
  
  costCentreChanged(value: CostCentre, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }
  
  costCentreCompare(a: any, b: any) {
    return COST_CENTRE_METADATA.equals(a, b);
  }
  
  

  approvalRejectionCommentChanging(value: string, unsetDependents: boolean = true) {}
  
  approvalRejectionCommentChanged(value: string, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  approvedChanging(value: boolean, unsetDependents: boolean = true) {}
  
  approvedChanged(value: boolean, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  approvedRejectedByChanging(value: string, unsetDependents: boolean = true) {}
  
  approvedRejectedByChanged(value: string, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  approvedRejectedDateChanging(value: Date, unsetDependents: boolean = true) {}
  
  approvedRejectedDateChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  approvedRejectedDateLessThanChanging(value: Date, unsetDependents: boolean = true) {}
  
  approvedRejectedDateLessThanChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  approvedRejectedDateLessThanOrEqualToChanging(value: Date, unsetDependents: boolean = true) {}
  
  approvedRejectedDateLessThanOrEqualToChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  approvedRejectedDateGreaterThanChanging(value: Date, unsetDependents: boolean = true) {}
  
  approvedRejectedDateGreaterThanChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  approvedRejectedDateGreaterThanOrEqualToChanging(value: Date, unsetDependents: boolean = true) {}
  
  approvedRejectedDateGreaterThanOrEqualToChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  commentsChanging(value: string, unsetDependents: boolean = true) {}
  
  commentsChanged(value: string, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  createdByChanging(value: string, unsetDependents: boolean = true) {}
  
  createdByChanged(value: string, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  dateCreatedChanging(value: Date, unsetDependents: boolean = true) {}
  
  dateCreatedChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  dateCreatedLessThanChanging(value: Date, unsetDependents: boolean = true) {}
  
  dateCreatedLessThanChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  dateCreatedLessThanOrEqualToChanging(value: Date, unsetDependents: boolean = true) {}
  
  dateCreatedLessThanOrEqualToChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  dateCreatedGreaterThanChanging(value: Date, unsetDependents: boolean = true) {}
  
  dateCreatedGreaterThanChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  dateCreatedGreaterThanOrEqualToChanging(value: Date, unsetDependents: boolean = true) {}
  
  dateCreatedGreaterThanOrEqualToChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  disabledTooltip(element) {
    if (!element) {
      return true;
    }
    return element.valid || !element.touched;
  }

  get transferAssetId() {
    return this.searchForm && this.searchForm.get('transferAssetId');
  }

  get asset() {
    return this.searchForm && this.searchForm.get('asset');
  }

  get businessUnit() {
    return this.searchForm && this.searchForm.get('businessUnit');
  }

  get costCentre() {
    return this.searchForm && this.searchForm.get('costCentre');
  }

  get approvalRejectionComment() {
    return this.searchForm && this.searchForm.get('approvalRejectionComment');
  }

  get approved() {
    return this.searchForm && this.searchForm.get('approved');
  }

  get approvedRejectedBy() {
    return this.searchForm && this.searchForm.get('approvedRejectedBy');
  }

  get approvedRejectedDate() {
    return this.searchForm && this.searchForm.get('approvedRejectedDate');
  }

  get approvedRejectedDateLessThan() {
    return this.searchForm && this.searchForm.get('approvedRejectedDateLessThan');
  }
  
  get approvedRejectedDateLessThanOrEqualTo() {
    return this.searchForm && this.searchForm.get('approvedRejectedDateLessThanOrEqualTo');
  }
  
  get approvedRejectedDateGreaterThan() {
    return this.searchForm && this.searchForm.get('approvedRejectedDateGreaterThan');
  }
  
  get approvedRejectedDateGreaterThanOrEqualTo() {
    return this.searchForm && this.searchForm.get('approvedRejectedDateGreaterThanOrEqualTo');
  }

  get comments() {
    return this.searchForm && this.searchForm.get('comments');
  }

  get createdBy() {
    return this.searchForm && this.searchForm.get('createdBy');
  }

  get dateCreated() {
    return this.searchForm && this.searchForm.get('dateCreated');
  }

  get dateCreatedLessThan() {
    return this.searchForm && this.searchForm.get('dateCreatedLessThan');
  }
  
  get dateCreatedLessThanOrEqualTo() {
    return this.searchForm && this.searchForm.get('dateCreatedLessThanOrEqualTo');
  }
  
  get dateCreatedGreaterThan() {
    return this.searchForm && this.searchForm.get('dateCreatedGreaterThan');
  }
  
  get dateCreatedGreaterThanOrEqualTo() {
    return this.searchForm && this.searchForm.get('dateCreatedGreaterThanOrEqualTo');
  }


}



